import { SwipeableDrawer, type SxProps, type Theme } from "@mui/material";
import { type ReactNode } from "react";

import { mergeSxProps } from "../../lib/mergeSxProps";
import { type UseModalState } from "../../lib/useModalState";

export interface BottomSheetProps {
  disableBackdropTransition?: boolean;
  disableSwipeArea?: boolean;
  canDismiss?: boolean;
  modalState: UseModalState;
  children: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  paperSx?: SxProps<Theme>;
  onClose?: () => void;
}

export function BottomSheet(props: BottomSheetProps) {
  const {
    modalState,
    children,
    header,
    footer,
    paperSx,
    onClose,
    disableSwipeArea = false,
    disableBackdropTransition = false,
    canDismiss = true,
  } = props;
  const { modalIsOpen, closeModal, openModal } = modalState;

  return (
    <SwipeableDrawer
      disableDiscovery
      anchor="bottom"
      // disable backdrop transition on Android to improve FPS on low-end devices
      // https://mui.com/material-ui/react-drawer/#swipeable
      disableBackdropTransition={disableBackdropTransition}
      SlideProps={{
        // to make sure that if drawer is open while mounted, it will still animate
        appear: true,
      }}
      // render bottom sheets at the same index as modals
      sx={(theme) => ({ zIndex: theme.zIndex.modal })}
      open={modalIsOpen}
      SwipeAreaProps={{
        sx: disableSwipeArea ? { display: "none" } : undefined,
      }}
      PaperProps={{
        sx: mergeSxProps(
          (theme) => ({
            backgroundColor: theme.palette.background.tertiary,
            borderRadius: theme.borderRadius?.large,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }),
          paperSx
        ),
      }}
      ModalProps={{ keepMounted: false }}
      // prevent the sheet from being dismissed by swiping if canDismiss is false
      onTouchStartCapture={(event) => {
        if (!canDismiss) {
          event.stopPropagation();
        }
      }}
      onClose={() => {
        if (!canDismiss) {
          return;
        }

        closeModal();
        onClose?.();
      }}
      onOpen={() => {
        openModal();
      }}
    >
      {header}

      {children}

      {footer}
    </SwipeableDrawer>
  );
}
